import { create } from 'zustand'
import type { MidiDevice, Preset, ExtraNote } from '../types'
import { getNoteNameFrench, getNoteName, calculateFrequencyFromTonic, calculateTonicOctave, getRelativeNoteIndex, getMidiNoteInOctave } from '../utils/noteUtils'
import * as api from '../lib/api'

interface TuningStore {
  // State
  noteOffsets: number[]
  referencePitch: number
  tonicNote: number // Note index 0-11 (C=0, C#=1, ..., B=11)
  tonicFrequency: number // Frequency in Hz for the tonic
  extraNotes: ExtraNote[] // Individually-tunable notes added before/after the main octave
  selectedNote: number | null
  selectedExtraNoteId: string | null
  activeNotes: Set<number>
  connectedDevice: MidiDevice | null
  availableDevices: MidiDevice[]
  currentPreset: string | null
  midiSupported: boolean

  // Actions
  setNoteOffset: (noteIndex: number, cents: number) => void
  resetNoteOffset: (noteIndex: number) => void
  setReferencePitch: (hz: number) => void
  setTonicNote: (noteIndex: number) => void
  setTonicFrequency: (hz: number) => void
  selectNote: (noteIndex: number | null) => void
  addActiveNote: (midiNote: number) => void
  removeActiveNote: (midiNote: number) => void
  setConnectedDevice: (device: MidiDevice | null) => void
  setAvailableDevices: (devices: MidiDevice[]) => void
  setMidiSupported: (supported: boolean) => void
  loadPreset: (preset: Preset) => void
  savePreset: (name: string) => Promise<void>
  deletePreset: (id: number) => Promise<void>
  setCurrentPreset: (name: string | null) => void
  applyLiveState: (state: api.TuningStateDto) => void
  newGamme: () => void
  addExtraNote: (noteIndex: number, octave: number) => void
  removeExtraNote: (id: string) => void
  setExtraNoteCents: (id: string, cents: number) => void
  resetExtraNoteCents: (id: string) => void
  selectExtraNote: (id: string | null) => void
}

// Debounced persistence of the "live" (unsaved) tuning state to the server,
// so rapid slider drags don't fire one request per pixel.
let saveStateTimer: ReturnType<typeof setTimeout> | null = null
function scheduleSaveLiveState(state: api.TuningStateDto) {
  if (saveStateTimer) clearTimeout(saveStateTimer)
  saveStateTimer = setTimeout(() => {
    api.putTuningState(state).catch((error) => console.error('Error saving tuning state:', error))
  }, 400)
}

function liveStateOf(state: {
  noteOffsets: number[]
  referencePitch: number
  tonicNote: number
  tonicFrequency: number
  extraNotes: ExtraNote[]
}): api.TuningStateDto {
  return {
    noteOffsets: state.noteOffsets,
    referencePitch: state.referencePitch,
    tonicNote: state.tonicNote,
    tonicFrequency: state.tonicFrequency,
    extraNotes: state.extraNotes,
  }
}

export const useTuningStore = create<TuningStore>()((set, get) => ({
  // Initial state (hydrated from the server once authenticated, see hydrateTuningState)
  noteOffsets: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  referencePitch: 440,
  tonicNote: 0,
  tonicFrequency: 261.63,
  extraNotes: [],
  selectedNote: null,
  selectedExtraNoteId: null,
  activeNotes: new Set(),
  connectedDevice: null,
  availableDevices: [],
  currentPreset: null,
  midiSupported: false,

  // Actions
  setNoteOffset: (noteIndex, cents) =>
    set((state) => {
      const newOffsets = [...state.noteOffsets]
      newOffsets[noteIndex] = Math.max(-300, Math.min(300, cents))
      scheduleSaveLiveState(liveStateOf({ ...state, noteOffsets: newOffsets }))
      return { noteOffsets: newOffsets, currentPreset: null }
    }),

  resetNoteOffset: (noteIndex) =>
    set((state) => {
      const newOffsets = [...state.noteOffsets]
      newOffsets[noteIndex] = 0
      scheduleSaveLiveState(liveStateOf({ ...state, noteOffsets: newOffsets }))
      return { noteOffsets: newOffsets, currentPreset: null }
    }),

  setReferencePitch: (hz) =>
    set((state) => {
      const newPitch = Math.max(415, Math.min(466, hz))
      scheduleSaveLiveState(liveStateOf({ ...state, referencePitch: newPitch }))
      return { referencePitch: newPitch, currentPreset: null }
    }),

  setTonicNote: (noteIndex) =>
    set((state) => {
      const newTonicNote = Math.max(0, Math.min(11, noteIndex))
      scheduleSaveLiveState(liveStateOf({ ...state, tonicNote: newTonicNote }))
      return { tonicNote: newTonicNote, currentPreset: null }
    }),

  setTonicFrequency: (hz) =>
    set((state) => {
      const newTonicFrequency = Math.max(100, Math.min(2000, hz))
      scheduleSaveLiveState(liveStateOf({ ...state, tonicFrequency: newTonicFrequency }))
      return { tonicFrequency: newTonicFrequency, currentPreset: null }
    }),

  selectNote: (noteIndex) => set({ selectedNote: noteIndex, selectedExtraNoteId: null }),

  addActiveNote: (midiNote) =>
    set((state) => ({
      activeNotes: new Set([...state.activeNotes, midiNote])
    })),

  removeActiveNote: (midiNote) =>
    set((state) => {
      const newActiveNotes = new Set(state.activeNotes)
      newActiveNotes.delete(midiNote)
      return { activeNotes: newActiveNotes }
    }),

  setConnectedDevice: (device) => set({ connectedDevice: device }),

  setAvailableDevices: (devices) => set({ availableDevices: devices }),

  setMidiSupported: (supported) => set({ midiSupported: supported }),

  loadPreset: (preset) =>
    set({
      noteOffsets: [...preset.noteOffsets],
      referencePitch: preset.referencePitch,
      tonicNote: preset.tonicNote ?? 0, // Default to C if not specified (for backward compatibility)
      tonicFrequency: preset.tonicFrequency ?? 261.63, // Default to C4 if not specified
      extraNotes: preset.extraNotes ? preset.extraNotes.map((n) => ({ ...n })) : [],
      currentPreset: preset.name,
      selectedNote: null,
      selectedExtraNoteId: null,
    }),

  savePreset: async (name) => {
    const state = get()
    await api.saveGamme({
      name,
      noteOffsets: state.noteOffsets,
      referencePitch: state.referencePitch,
      tonicNote: state.tonicNote,
      tonicFrequency: state.tonicFrequency,
      extraNotes: state.extraNotes,
    })
    set({ currentPreset: name })
  },

  deletePreset: async (id) => {
    await api.deleteGamme(id)
    set({ currentPreset: null })
  },

  setCurrentPreset: (name) => set({ currentPreset: name }),

  applyLiveState: (state) =>
    set({
      noteOffsets: [...state.noteOffsets],
      referencePitch: state.referencePitch,
      tonicNote: state.tonicNote,
      tonicFrequency: state.tonicFrequency,
      extraNotes: state.extraNotes ? state.extraNotes.map((n) => ({ ...n })) : [],
    }),

  newGamme: () => {
    const blank: api.TuningStateDto = {
      noteOffsets: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      referencePitch: 440,
      tonicNote: 0,
      tonicFrequency: 261.63,
      extraNotes: [],
    }
    scheduleSaveLiveState(blank)
    set({ ...blank, currentPreset: null, selectedNote: null, selectedExtraNoteId: null })
  },

  addExtraNote: (noteIndex, octave) =>
    set((state) => {
      const existing = state.extraNotes.find((n) => n.noteIndex === noteIndex && n.octave === octave)
      if (existing) {
        return { selectedExtraNoteId: existing.id, selectedNote: null }
      }
      const newNote: ExtraNote = {
        id: crypto.randomUUID(),
        noteIndex,
        octave,
        cents: 0,
      }
      const newExtraNotes = [...state.extraNotes, newNote]
      scheduleSaveLiveState(liveStateOf({ ...state, extraNotes: newExtraNotes }))
      return { extraNotes: newExtraNotes, currentPreset: null, selectedExtraNoteId: newNote.id, selectedNote: null }
    }),

  removeExtraNote: (id) =>
    set((state) => {
      const newExtraNotes = state.extraNotes.filter((n) => n.id !== id)
      scheduleSaveLiveState(liveStateOf({ ...state, extraNotes: newExtraNotes }))
      return {
        extraNotes: newExtraNotes,
        currentPreset: null,
        selectedExtraNoteId: state.selectedExtraNoteId === id ? null : state.selectedExtraNoteId,
      }
    }),

  setExtraNoteCents: (id, cents) =>
    set((state) => {
      const clamped = Math.max(-300, Math.min(300, cents))
      const newExtraNotes = state.extraNotes.map((n) => (n.id === id ? { ...n, cents: clamped } : n))
      scheduleSaveLiveState(liveStateOf({ ...state, extraNotes: newExtraNotes }))
      return { extraNotes: newExtraNotes, currentPreset: null }
    }),

  resetExtraNoteCents: (id) => get().setExtraNoteCents(id, 0),

  selectExtraNote: (id) => set({ selectedExtraNoteId: id, selectedNote: null }),
}))

// Cents offset that applies to a given absolute MIDI note: an individually
// tuned extra note if one was added at that exact octave/note, otherwise the
// note's cents from the repeating 12-note pattern.
export function getCentsForMidiNote(midiNote: number): number {
  const state = useTuningStore.getState()
  const extra = state.extraNotes.find((n) => getMidiNoteInOctave(n.octave, n.noteIndex) === midiNote)
  if (extra) return extra.cents
  return state.noteOffsets[midiNote % 12]
}

// Fetches the last live (unsaved) tuning state from the server and applies it.
// Call once after authentication, on app startup.
export async function hydrateTuningState(): Promise<void> {
  try {
    const state = await api.getTuningState()
    useTuningStore.getState().applyLiveState(state)
  } catch (error) {
    console.error('Error hydrating tuning state:', error)
  }
}

export function getAllPresets(): Promise<Preset[]> {
  return api.getGammes()
}

export function exportPreset(preset: Preset): string {
  return JSON.stringify(preset, null, 2)
}

export function importPreset(json: string): Preset {
  const preset = JSON.parse(json)
  if (!preset.name || !preset.noteOffsets || !Array.isArray(preset.noteOffsets)) {
    throw new Error('Invalid preset format')
  }
  // Ensure tonicNote and tonicFrequency have default values if missing (for backward compatibility)
  return {
    ...preset,
    tonicNote: preset.tonicNote ?? 0,
    tonicFrequency: preset.tonicFrequency ?? 261.63,
    extraNotes: preset.extraNotes ?? []
  }
}

export interface TunerNote {
  name: string // French note name
  nameEn: string // English note name
  cents: number // Offset in cents (tonic is always 0)
  frequency: number // Frequency in Hz
  relativeIndex: number // Position relative to tonic (0-11)
  midiNote: number // Absolute MIDI note, used to order notes by pitch/octave
  isTonic: boolean
  isExtra: boolean // True for notes added before/after the main octave
}

export interface TunerExport {
  tonic: {
    name: string // French note name
    nameEn: string // English note name
    frequency: number // Frequency in Hz
    cents: number // Always 0
  }
  notes: TunerNote[] // All 12 notes with their offsets
  date: string
  presetName?: string // Optional preset name
}

export function exportTunerFormat(
  noteOffsets: number[],
  tonicNote: number,
  tonicFrequency: number,
  referencePitch: number = 440,
  presetName?: string,
  extraNotes: ExtraNote[] = []
): TunerExport {
  const tonicOctave = calculateTonicOctave(tonicNote, tonicFrequency, referencePitch)

  const notes: TunerNote[] = []

  // Generate all 12 notes
  for (let absoluteIndex = 0; absoluteIndex < 12; absoluteIndex++) {
    const relativeIndex = getRelativeNoteIndex(absoluteIndex, tonicNote)
    const offset = noteOffsets[absoluteIndex]

    // Calculate MIDI note for this note (same octave as tonic for now, will be adjusted)
    let targetOctave = tonicOctave
    if (absoluteIndex < tonicNote) {
      targetOctave = tonicOctave + 1
    }
    const midiNote = targetOctave * 12 + absoluteIndex + 12

    // Calculate frequency
    const frequency = calculateFrequencyFromTonic(
      midiNote,
      offset,
      tonicNote,
      tonicFrequency,
      referencePitch
    )

    notes.push({
      name: getNoteNameFrench(absoluteIndex),
      nameEn: getNoteName(absoluteIndex),
      cents: absoluteIndex === tonicNote ? 0 : offset, // Tonic is always 0
      frequency: frequency,
      relativeIndex: relativeIndex,
      midiNote: midiNote,
      isTonic: absoluteIndex === tonicNote,
      isExtra: false
    })
  }

  // Add extra notes (added before/after the main octave)
  extraNotes.forEach((extraNote) => {
    const midiNote = getMidiNoteInOctave(extraNote.octave, extraNote.noteIndex)
    const frequency = calculateFrequencyFromTonic(
      midiNote,
      extraNote.cents,
      tonicNote,
      tonicFrequency,
      referencePitch
    )
    notes.push({
      name: `${getNoteNameFrench(extraNote.noteIndex)}${extraNote.octave}`,
      nameEn: `${getNoteName(extraNote.noteIndex)}${extraNote.octave}`,
      cents: extraNote.cents,
      frequency: frequency,
      relativeIndex: getRelativeNoteIndex(extraNote.noteIndex, tonicNote),
      midiNote: midiNote,
      isTonic: false,
      isExtra: true
    })
  })

  // Order everything (base notes and extra notes together) by actual pitch,
  // low to high, so extra notes fall in their real position in the octave
  // range rather than being tacked on at the end.
  notes.sort((a, b) => a.midiNote - b.midiNote)

  return {
    tonic: {
      name: getNoteNameFrench(tonicNote),
      nameEn: getNoteName(tonicNote),
      frequency: tonicFrequency,
      cents: 0
    },
    notes: notes,
    date: new Date().toISOString(),
    presetName: presetName
  }
}
